import { ref, computed } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

/**
 * Cerimônia da remoção de contato, compartilhada pelos dois lugares onde ela
 * existe: o diálogo da página de Contatos e o `woot-delete-modal` do painel da
 * conversa. Sem isto, cada tela teria a sua versão da mesma regra — e uma delas
 * ficaria para trás.
 *
 * O que a tela precisa saber antes de confirmar:
 *  - quantas conversas concluídas serão PRESERVADAS;
 *  - quantas conversas em aberto serão ENCERRADAS e preservadas;
 *  - quantas serão APAGADAS — só as privadas de conexão pessoal sem atendente,
 *    a única exceção que o §7.4 do manual de regras abre;
 *  - se é preciso digitar o código, e qual é ele.
 *
 * O código é gerado pelo servidor e validado lá também. Aqui ele é só o que a
 * pessoa lê e redigita: serve para tirar a exclusão do piloto automático quando
 * há perda real, não para autenticar ninguém.
 */
export function useContactRemovalConfirmation() {
  const isLoadingPreview = ref(false);
  const resolvedConversations = ref(0);
  const openConversations = ref(0);
  const purgedConversations = ref(0);
  const historyPreserved = ref(false);
  const requiresCode = ref(false);
  const expectedCode = ref('');
  const typedCode = ref('');
  const previewFailed = ref(false);

  const reset = () => {
    isLoadingPreview.value = false;
    resolvedConversations.value = 0;
    openConversations.value = 0;
    purgedConversations.value = 0;
    historyPreserved.value = false;
    requiresCode.value = false;
    expectedCode.value = '';
    typedCode.value = '';
    previewFailed.value = false;
  };

  const loadPreview = async contactId => {
    if (!contactId) return;
    reset();
    isLoadingPreview.value = true;
    try {
      const { data } = await ContactAPI.removalPreview(contactId);
      resolvedConversations.value = data.resolved_conversations || 0;
      openConversations.value = data.open_conversations || 0;
      purgedConversations.value = data.purged_conversations || 0;
      historyPreserved.value = Boolean(data.history_preserved);
      requiresCode.value = Boolean(data.requires_code);
      expectedCode.value = data.confirmation_code || '';
    } catch (error) {
      // Sem a prévia não dá para prometer nada à pessoa. Deixar o botão livre
      // seria pior: ela confirmaria sem o aviso. O diálogo trata este estado.
      previewFailed.value = true;
    } finally {
      isLoadingPreview.value = false;
    }
  };

  const codeMatches = computed(
    () => typedCode.value.trim() === expectedCode.value.trim()
  );

  const canConfirm = computed(() => {
    if (isLoadingPreview.value || previewFailed.value) return false;
    return requiresCode.value ? codeMatches.value : true;
  });

  const confirmationCode = computed(() =>
    requiresCode.value ? typedCode.value.trim() : undefined
  );

  return {
    isLoadingPreview,
    resolvedConversations,
    openConversations,
    purgedConversations,
    historyPreserved,
    requiresCode,
    expectedCode,
    typedCode,
    previewFailed,
    codeMatches,
    canConfirm,
    confirmationCode,
    loadPreview,
    reset,
  };
}

export default useContactRemovalConfirmation;
